import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { REPORT_INDEX } from '@/lib/report-index';
import { canAccessPath } from '@/lib/auth';

/**
 * Find a report: type a question, a metric or a slice ("cost per new customer
 * by campaign", "cac", "landing page") and land on the one place that answers
 * it. Replaces "Where to look", whose static list went stale as pages moved.
 * Opened from the sidebar or with the / key anywhere outside a text field.
 */
const EVT = 'mm:find-report';
export const openFindReport = () => window.dispatchEvent(new Event(EVT));

const pathOf = (to) => String(to).split('?')[0];
// The permission a destination needs: an explorer path rides its explorer's gate.
const GATES = {
  '/reports/marketing': '/growth/campaigns', '/reports/channels': '/commerce/attribution',
  '/reports/products': '/commerce/insights', '/reports/customers': '/growth/customer-value',
  '/reports/leads': '/tracking/attribution', '/reports/found': '/growth/findings',
};
const allowed = (to) => {
  const p = pathOf(to);
  const base = Object.keys(GATES).find((k) => p === k || p.startsWith(`${k}/`));
  try { return canAccessPath(base ? GATES[base] : p); } catch { return true; }
};

export function FindReport({ ecomOn }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    const show = () => setOpen(true);
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(input|textarea|select)$/i.test(t.tagName))) return;
      e.preventDefault();
      setOpen(true);
    };
    window.addEventListener(EVT, show);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener(EVT, show); window.removeEventListener('keydown', onKey); };
  }, []);

  // A lead desk is offered lead answers; a store, store answers.
  const rows = useMemo(() => REPORT_INDEX.filter((r) => {
    const p = pathOf(r.to);
    const leadOnly = p.startsWith('/reports/leads') || p === '/tracking/email';
    const storeOnly = !leadOnly && (p.startsWith('/reports/') || p.startsWith('/commerce') || p.startsWith('/growth') || p === '/signal-health' || p === '/number-check' || p === '/value-report' || p === '/studio');
    if (ecomOn === false && storeOnly) return false;
    if (ecomOn === true && p.startsWith('/reports/leads')) return false;
    return allowed(r.to);
  }), [ecomOn]);

  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Find a report: a question, a number or a slice (cost per new customer, landing page, ROAS…)" />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>
          <span className="inline-flex items-center gap-2 text-muted-foreground"><Search className="size-4" /> Nothing answers that yet. Try a metric name, such as ROAS or conversion rate.</span>
        </CommandEmpty>
        <CommandGroup heading="Where to find it">
          {rows.map((r) => (
            <CommandItem key={`${r.to}|${r.q}`} value={`${r.q} ${r.words} ${r.where}`} onSelect={() => go(r.to)} className="gap-3">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-medium text-foreground">{r.q}</span>
                <span className="block truncate text-[12.5px] text-muted-foreground">{r.where}</span>
              </span>
              <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
